import {computed, defineComponent, onMounted, reactive, ref, watch} from '@vue/composition-api';
import {SettingsContext} from "@/utilities/settings-context";
import {Page, NavbarBoxModel, PageContext, BreadcrumbSegment, PageAction} from "@/components/navbar-box/page-context";
import ClickOutside from "vue-click-outside";
import {getName, isAuthenticated, onLogIn, onLogOut} from "@/functions/authentication-functions";
import {i18n} from "@/utilities/vue-i18n";
import * as ImBox from '@/utilities/imbox';
import {sanitizeHtml} from "@/utilities/html-modifier";
import AdminNav from '@/components/admin-nav/admin-nav.vue';
import {navigateToSettings} from "@/functions/navigation-functions";

export default defineComponent({
    components: {
        AdminNav,
    },
    directives: {
        ClickOutside
    },
    setup(_, context) {

        const model = reactive<NavbarBoxModel>({

            pageContext: null as unknown as PageContext

        }) as NavbarBoxModel;

        const homeCrumb = {text: i18n.t('pages.home.title') as string, icon: '<i class="mdi mdi-home-city"></i>', route: '/'};

        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        const settings = reactive({

            open: false,

            userOpened: false,

            hasSubBar: false,

            loggedIn: isAuthenticated(),

            plain: computed(() => context.root.$route.meta?.plain === true),

            breadcrumb: computed(() => {

                const breadcrumb = new Array<BreadcrumbSegment>();

                if (context.root.$route.name === 'Home')
                {
                    breadcrumb.push({...homeCrumb, route: undefined});
                }
                else
                {
                    if(model.pageContext?.breadcrumb?.showHome !== false)
                    {
                        breadcrumb.push(homeCrumb);
                    }

                    if (model?.pageContext?.breadcrumb)
                    {
                        return [...breadcrumb, ...model.pageContext.breadcrumb.segments];
                    }
                }

                return breadcrumb;
            }),

            actions: computed(() => {

                const pageActions = [...(model.pageContext && model.pageContext.actions && model.pageContext.actions.length > 0 ? model.pageContext.actions as PageAction[] : [])];

                if (context.root.$route?.params?.company != null && context.root.$route?.name !== 'Settings Page')
                {
                    pageActions.push(...[...(pageActions.length > 0 ? '-' : []), {

                        name: `<i class="mdi mdi-office-home-cog"></i><b>Placeholder</b>`,

                        action: () => navigateToSettings(context)

                    }] as PageAction[]);
                }

                return pageActions;
            }),

            actionAvailable: computed(() => settings.actions.length > 0),

            helpId: '0',

            name: getName(),

            logInPage: computed(() => context.root.$route.name === 'Sign In'),

            hiddenActions: computed(() => context.root.$route.meta?.anonymous && (!model.pageContext || settings.actions.length === 0))
        });

        watch(() => [settings.breadcrumb, model.pageContext?.title?.text], () => {

            document.title = `${SettingsContext.pageTitle}${(model.pageContext?.title?.text ? ` | ${model.pageContext?.title?.text}` : settings.breadcrumb && settings.breadcrumb.length > 0 ? ` | ${settings.breadcrumb[settings.breadcrumb.length - 1].text}` : '')}`;

        }, {deep: true});

        const logoUrl = computed(() => settings.breadcrumb && settings.breadcrumb.length > 0 && settings.breadcrumb[0].route ? settings.breadcrumb[0].route : '/');

        const title = computed(() => model.pageContext?.title?.text ? model.pageContext?.title?.text : settings.breadcrumb && settings.breadcrumb.length > 0 ? settings.breadcrumb[settings.breadcrumb.length - 1].text : '…') as unknown as string;

        const titleAction = computed(() => ({ icon : model.pageContext?.title?.icon, action: model.pageContext?.title?.action, iconII:  model.pageContext?.title?.iconII, actionII: model.pageContext?.title?.actionII}) );

        const details = ref<Element>(null as unknown as Element);

        const updateSubBar = () => {

            model.pageContext = (context?.parent?.$refs.page as Page)?.pageContext;

            if(model.pageContext?.controls && details.value?.firstChild) {

                if (!(model.pageContext.controls as unknown as Element).classList.contains('page-controls')) {

                    console.error('[The Office]: The "controls" element must have the "page-controls" class to bo rendered correctly.');

                    settings.hasSubBar = false;

                    return;
                }

                (details.value?.firstChild as Element).replaceWith(model.pageContext.controls as unknown as Node);

                settings.hasSubBar = true;
            }
            else
            {
                settings.hasSubBar = false;
            }
        };

        onMounted(() => {

            watch(() => [context.root.$route, (context?.parent?.$refs.page as Page)?.pageContext?.controls], () => {

                updateSubBar();

            }, {deep: true});

            context.root.$nextTick(() => updateSubBar());
        });

        onLogIn(() => {

            settings.loggedIn = isAuthenticated();

            settings.name = getName();
        });

        onLogOut(() => {

            settings.loggedIn = false;

            settings.name = null;
        });

        const contentText = document.createElement('span');

        const renderHtml = (html: string) => {

            contentText.innerHTML = html;

            return contentText.textContent;
        }

        return {

            model,

            settings,

            details,

            logoUrl,

            title,

            titleAction,

            openHelp: () => ImBox.trigger(),

            sanitizeHtml,

            renderHtml
        }
    }
});
